import ComponentWithSocket from '../../../lib/component';
import { Component, Watch } from 'vue-property-decorator';
import moment from 'moment';

import * as Store from '../../../store';
import { saveXlsx } from '../../../utils/xlsx';
import {
    USER_EXPORT_FIELDS,
    DEFAULT_USER_EXPORT_FIELDS,
    buildUsersSheet,
} from '../../../utils/users-export';

/**
 * Filtrele listei. Valorile rolurilor si ale cumparaturilor sunt aceleasi
 * string-uri ca enum-urile `ParticipationRole` / `PurchaseStatus` din API.
 */
interface UserFilters {
    auctionIds: string[];
    auctionRole: string;
    authorIds: string[];
    authorRole: string;
    purchaseStatus: string;
}

interface FilterOption {
    label: string;
    value: string;
}

const AUCTION_ROLE_OPTIONS = [
    { value: 'registered', text: 'Înscris' },
    { value: 'bidder', text: 'Ofertant' },
    { value: 'winner', text: 'Câștigător' },
];

const AUTHOR_ROLE_OPTIONS = [
    { value: 'bidder', text: 'Ofertant' },
    { value: 'winner', text: 'Câștigător' },
];

const ROLE_HINTS: { [role: string]: string } = {
    registered: 'S-a înscris la licitație, cu sau fără ofertă.',
    bidder: 'A dat cel puțin o ofertă.',
    winner: 'A câștigat cel puțin un lot.',
};

const PURCHASE_STATUS_OPTIONS = [
    { value: '', text: 'Toți utilizatorii' },
    { value: 'buyer', text: 'Cumpărători' },
    { value: 'non-buyer', text: 'Fără cumpărături' },
];

function emptyFilters(): UserFilters {
    return {
        auctionIds: [],
        auctionRole: 'bidder',
        authorIds: [],
        authorRole: 'bidder',
        purchaseStatus: '',
    };
}

@Component
export default class UsersList extends ComponentWithSocket {
    public data: {
        users: any[];
        qrUserOpen: boolean;
        qrUserId: string;
        pushUserOpen: boolean;
        pushUserId: string;
        pushUserSubject: string;
        pushUserContent: string;
        pushUserData: string;
        perPage: number;
        currentPage: number;
        usersCount: number;
        searchQuery: string;
        isSearching: boolean;
        filters: UserFilters;
        auctionOptions: FilterOption[];
        authorOptions: FilterOption[];
        exportOpen: boolean;
        exportFields: string[];
        bidsOpen: boolean;
        bidsUserName: string;
        bidsWon: any[];
        bidsAll: any[];
        bidsCancelled: any[];
    } = {
        users: [],
        qrUserOpen: false,
        qrUserId: '',
        pushUserOpen: false,
        pushUserId: '',
        pushUserSubject: '',
        pushUserContent: '',
        pushUserData: '',
        perPage: 50,
        currentPage: 1,
        usersCount: 0,
        searchQuery: this.$attrs.s ? this.$attrs.s : '',
        isSearching: false,
        filters: emptyFilters(),
        auctionOptions: [],
        authorOptions: [],
        exportOpen: false,
        exportFields: DEFAULT_USER_EXPORT_FIELDS.slice(),
        bidsOpen: false,
        bidsUserName: '',
        bidsWon: [],
        bidsAll: [],
        bidsCancelled: [],
    };

    get tableFields() {
        return [
            { key: 'avatar', label: '' },
            { key: 'numar_licitare', label: 'Numar licitare' },
            { key: 'nume', label: 'Nume' },
            { key: 'contact', label: 'Contact' },
            { key: 'createdAt', label: 'Data' },
            { key: 'verificat', label: 'Verificat' },
            { key: 'actiuni', label: 'Actiuni' },
        ];
    }

    get auctionRoleOptions() {
        return AUCTION_ROLE_OPTIONS;
    }

    get authorRoleOptions() {
        return AUTHOR_ROLE_OPTIONS;
    }

    get purchaseStatusOptions() {
        return PURCHASE_STATUS_OPTIONS;
    }

    get roleHints() {
        return ROLE_HINTS;
    }

    get exportFieldOptions() {
        return USER_EXPORT_FIELDS.map((field) => ({
            value: field.key,
            text: field.label,
        }));
    }

    /**
     * Payload-ul trimis catre API: doar filtrele setate. Rolul conteaza doar
     * cand exista licitatii sau autori alesi.
     */
    get activeFilters() {
        const filters = this.data.filters;
        const payload: any = {};

        if (this.data.searchQuery) {
            payload.searchQuery = this.data.searchQuery;
        }

        if (filters.auctionIds.length) {
            payload.auctionIds = filters.auctionIds;
            payload.auctionRole = filters.auctionRole;
        }

        if (filters.authorIds.length) {
            payload.authorIds = filters.authorIds;
            payload.authorRole = filters.authorRole;
        }

        if (filters.purchaseStatus) {
            payload.purchaseStatus = filters.purchaseStatus;
        }

        return payload;
    }

    get hasActiveFilters() {
        const filters = this.data.filters;

        return (
            filters.auctionIds.length > 0 ||
            filters.authorIds.length > 0 ||
            !!filters.purchaseStatus
        );
    }

    @Watch('data.searchQuery')
    onSearchQueryChanged(val: string, oldVal: string) {
        if (!this.data.isSearching) {
            setTimeout(() => {
                this.data.isSearching = false;
                this.data.currentPage = 1;
                this.insertParam('s', this.data.searchQuery);

                this.reloadUsers(1);
            }, 1000);
        }
        this.data.isSearching = true;
    }

    @Watch('data.filters', { deep: true })
    onFiltersChanged() {
        this.data.currentPage = 1;
        this.reloadUsers(1);
    }

    constructor() {
        super();

        this.wsListener.onGet('admin.users', (success: boolean, data: any) => {
            Store.commitLoaderVisibility(this.$store, false);

            if (success) {
                this.data.users = data.users;
                this.data.usersCount = data.usersCount;
            }
        });

        this.wsListener.onGet(
            'admin.users.export',
            (success: boolean, data: any) => {
                Store.commitLoaderVisibility(this.$store, false);

                if (!success) {
                    return alert(data);
                }

                saveXlsx(
                    [buildUsersSheet(data.users, this.data.exportFields)],
                    `utilizatori-${moment().format('YYYY-MM-DD')}.xlsx`,
                );
            },
        );

        this.wsListener.onGet('auctions', (success: boolean, data: any) => {
            if (!success) {
                return;
            }

            // Cele mai noi sus.
            this.data.auctionOptions = data
                .slice()
                .reverse()
                .map((auction: any) => ({
                    label: auction.startDate
                        ? `${auction.name} — ${moment(auction.startDate).format('DD.MM.YYYY')}`
                        : auction.name,
                    value: auction._id,
                }));
        });

        this.wsListener.onGet(
            'admin.depositors.contracts.authors',
            (success: boolean, data: any) => {
                if (!success) {
                    return;
                }

                this.data.authorOptions = data
                    .filter((author: any) => author.name)
                    .map((author: any) => ({
                        label: author.name,
                        value: author._id,
                    }))
                    .sort((a: FilterOption, b: FilterOption) =>
                        a.label.localeCompare(b.label, 'ro'),
                    );
            },
        );

        this.wsListener.onGet('admin.users.bids', (success: boolean, data: any) => {
            Store.commitLoaderVisibility(this.$store, false);

            if (success) {
                this.data.bidsWon = data.won;
                this.data.bidsAll = data.bidded;
                this.data.bidsCancelled = data.cancelled || [];
            }
        });

        this.wsListener.onPost(
            'admin.users.push',
            (success: boolean, data: any) => {
                if (!success) {
                    return alert(data);
                }

                this.data.pushUserId = '';
                this.data.pushUserSubject = '';
                this.data.pushUserContent = '';
                this.data.pushUserData = '';
                this.data.pushUserOpen = false;
            },
        );
    }

    public reloadUsers(page: any = this.data.currentPage) {
        Store.commitLoaderVisibility(this.$store, true);

        this.wsListener.sendGet('admin.users', {
            ...this.activeFilters,
            perPage: this.data.perPage,
            currentPage: page,
        });
    }

    beforeRouteLeave(to: any, from: any, next: any) {
        this._beforeLeave(to, from);
        next();
    }

    mounted() {
        this.reloadUsers(1);

        this.wsListener.sendGet('auctions');
        this.wsListener.sendGet('admin.depositors.contracts.authors');
    }

    resetFilters() {
        this.data.filters = emptyFilters();
    }

    /**
     * Exportul foloseste aceleasi filtre ca lista, dar fara paginare - de
     * aceea are eveniment propriu, ca raspunsul sa nu inlocuiasca pagina
     * curenta din tabel.
     */
    doExport() {
        Store.commitLoaderVisibility(this.$store, true);

        this.wsListener.sendGet('admin.users.export', this.activeFilters);
    }

    qrUserOpen(user: any) {
        this.data.qrUserOpen = true;
        this.data.qrUserId = user._id;
    }

    openPushNotification(user: any) {
        this.data.pushUserId = user._id;
        this.data.pushUserOpen = true;
    }

    openBidsHistory(user: any) {
        this.data.bidsUserName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
        this.data.bidsWon = [];
        this.data.bidsAll = [];
        this.data.bidsCancelled = [];
        this.data.bidsOpen = true;

        Store.commitLoaderVisibility(this.$store, true);
        this.wsListener.sendGet('admin.users.bids', { userId: user._id });
    }

    sendPushNotification() {
        this.wsListener.sendPost('admin.users.push', {
            userId: this.data.pushUserId,
            title: this.data.pushUserSubject,
            content: this.data.pushUserContent,
            data: this.data.pushUserData,
        });
    }

    formatDate(dateString: string): string {
        return moment(dateString).format('DD-MM-YYYY HH:mm');
    }

    public insertParam(key: any, value: any) {
        key = encodeURIComponent(key);
        value = encodeURIComponent(value);

        // kvp looks like ['key1=value1', 'key2=value2', ...]
        let kvp = document.location.search.substr(1).split('&');
        let i = 0;

        for (; i < kvp.length; i++) {
            if (kvp[i].startsWith(key + '=')) {
                let pair = kvp[i].split('=');
                pair[1] = value;
                kvp[i] = pair.join('=');
                break;
            }
        }

        if (i >= kvp.length) {
            kvp[kvp.length] = [key, value].join('=');
        }

        // can return this or...
        let params = kvp.join('&');

        // reload page with new params
        window.history.replaceState(undefined, '', '?' + params);
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/users/list/index.ts